import React, { type ButtonHTMLAttributes, forwardRef } from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import Spinner from "@shared/spinner";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  children: React.ReactNode;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => Promise<unknown> | unknown;
  isDisabled?: boolean;
  size?: "sm" | "md" | "lg";
  type?: "button" | "submit" | "reset";
  variant?: "primary" | "secondary" | "tertiary" | "danger";
  tooltip?: string;
  loading?: boolean;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      children,
      onClick,
      isDisabled,
      size = "sm",
      type = "button",
      variant,
      tooltip,
      loading,
      fullWidth = false,
      style,
      ...restProps
    },
    ref,
  ) => {
    const buttonRender = (
      <button
        ref={ref}
        className={clsx(
          "button",
          {
            sm: size === "sm",
            md: size === "md",
            lg: size === "lg",
            "cursor-not-allowed": isDisabled,
            "opacity-50": isDisabled,
          },
          variant && `${variant}-button`,
          fullWidth && "w-full",
          className,
        )}
        /* This fixes a tooltip bug where the tooltip would not disappear if the button was disabled
           https://github.com/react-component/tooltip/issues/18 */
        style={{ pointerEvents: isDisabled ? "none" : "auto", ...style }}
        disabled={isDisabled || loading}
        onClick={onClick}
        type={type}
        {...restProps}
      >
        {loading && (
          <Spinner
            colorClassname="text-white-400 mr-2"
            style={{ verticalAlign: "-4px" }}
          />
        )}
        {children}
      </button>
    );

    if (tooltip) {
      return (
        <GenericTooltip content={tooltip} noInfoCircle>
          {buttonRender}
        </GenericTooltip>
      );
    }

    return buttonRender;
  },
);
